import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, updateDoc, addDoc, deleteDoc, collection, getDocs, getDoc, setDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  IoChevronBack, IoChevronForward, IoAdd, IoClose,
  IoChevronDown, IoChevronUp, IoBusiness,
  IoCalendarOutline, IoMapOutline, IoLocationSharp, IoEyeOffOutline, IoEyeOutline, IoTrashOutline, IoColorPaletteOutline,
} from 'react-icons/io5';

const PALETTE = [
  '#4A6741', '#5C7A8A', '#E85830', '#6B5B7B', '#C5A55A',
  '#3B6DAA', '#2A8C6A', '#C44D7B', '#6B8E23', '#B8860B',
  '#F5C518', '#1E2A32',
];
const getCompanyColor = (name, idx) => PALETTE[idx % PALETTE.length];

// 지도뷰용 좌표 (대한민국 주요 도시 — SVG 비율 기준)
const CITY_POSITIONS = {
  '서울': { x: 55, y: 15 },
  '경기': { x: 38, y: 25 },
  '인천': { x: 28, y: 18 },
  '부산': { x: 78, y: 75 },
  '대구': { x: 72, y: 58 },
  '광주': { x: 32, y: 68 },
  '대전': { x: 52, y: 45 },
  '세종': { x: 45, y: 38 },
  '제주': { x: 45, y: 90 },
};

// 주소에서 지역 추출
const extractRegion = (address) => {
  if (!address) return '기타';
  const match = address.match(/^(서울|경기|인천|부산|대구|광주|대전|세종|제주|울산|충북|충남|전북|전남|경북|경남|강원)/);
  return match ? match[1] : '기타';
};

const AdminSchedulePage = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [treeOpen, setTreeOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [modalForm, setModalForm] = useState({ companyName: '', title: '', date: '', time: '09:00', address: '', memo: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [allSchedules, setAllSchedules] = useState([]);
  const [companies, setCompanies] = useState([]); // 동적 업체 목록
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [savedColors, setSavedColors] = useState({}); // Firestore에서 로드된 업체별 색상
  const [colorPickerTarget, setColorPickerTarget] = useState(null); // 색상 변경 대상 업체

  // Firestore에서 업체별 색상 로드
  useEffect(() => {
    const loadColors = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'scheduleColors'));
        if (snap.exists()) setSavedColors(snap.data());
      } catch (err) {
        console.error('색상 로드 실패:', err);
      }
    };
    loadColors();
  }, []);

  const saveCompanyColor = async (companyName, color) => {
    const updated = { ...savedColors, [companyName]: color };
    setSavedColors(updated);
    setColorPickerTarget(null);
    try {
      await setDoc(doc(db, 'settings', 'scheduleColors'), updated);
    } catch (err) {
      console.error('색상 저장 실패:', err);
    }
  };

  const getResolvedColor = (name, idx) => savedColors[name] || PALETTE[idx % PALETTE.length];

  // Firestore에서 일정 로드
  useEffect(() => {
    const loadSchedules = async () => {
      setLoadingSchedules(true);
      try {
        const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
        const monthEnd = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
        const q = query(collection(db, 'schedules'),
          where('date', '>=', monthStart),
          where('date', '<=', monthEnd)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // 업체 목록 동적 추출
        const companyMap = {};
        list.forEach(s => {
          const name = s.companyName || '미지정';
          if (!companyMap[name]) {
            companyMap[name] = { name, region: extractRegion(s.address) };
          }
        });
        const companyList = Object.values(companyMap).map((c, i) => ({
          ...c,
          id: c.name,
          color: getResolvedColor(c.name, i),
        }));
        setCompanies(companyList);

        // 일정에 color, companyRegion 부여
        const enriched = list.map(s => {
          const comp = companyList.find(c => c.name === s.companyName);
          return {
            ...s,
            color: comp?.color || '#8C9AA4',
            companyRegion: comp?.region || extractRegion(s.address),
            site: s.title || '',
            type: '',
          };
        });
        setAllSchedules(enriched);
      } catch (err) {
        console.error('일정 로드 실패:', err);
        setAllSchedules([]);
      } finally {
        setLoadingSchedules(false);
      }
    };
    loadSchedules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, savedColors]);

  const toggleHidden = async (id) => {
    const target = allSchedules.find(s => s.id === id);
    const newHidden = !target?.hidden;
    // UI 즉시 반영
    setAllSchedules(prev => prev.map(s => s.id === id ? { ...s, hidden: newHidden } : s));
    // Firestore 업데이트
    try {
      await updateDoc(doc(db, 'schedules', id), { hidden: newHidden });
    } catch (err) {
      console.error('숨김 상태 저장 실패:', err);
      setAllSchedules(prev => prev.map(s => s.id === id ? { ...s, hidden: !newHidden } : s));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 날짜의 일정을 삭제하시겠습니까?')) return;
    setAllSchedules(prev => prev.filter(s => s.id !== id));
    try {
      await deleteDoc(doc(db, 'schedules', id));
    } catch (err) {
      console.error('일정 삭제 실패:', err);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('이 기간의 모든 일정을 삭제하시겠습니까?')) return;
    setAllSchedules(prev => prev.filter(s => s.groupId !== groupId));
    try {
      const q = query(collection(db, 'schedules'), where('groupId', '==', groupId));
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        await deleteDoc(doc(db, 'schedules', d.id));
      }
    } catch (err) {
      console.error('기간 일정 삭제 실패:', err);
    }
  };

  const hiddenIds = new Set(allSchedules.filter(s => s.hidden).map(s => s.id));
  const schedules = selectedCompany === 'all'
    ? allSchedules
    : allSchedules.filter(s => s.companyName === selectedCompany);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const weeks = [];
  let week = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }

  const getSchedulesForDay = (d) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return schedules.filter(s => s.date === dateStr).sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  };

  const prevMonth = () => { if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1); setSelectedDate(null); };
  const nextMonth = () => { if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1); setSelectedDate(null); };

  const selectedSchedules = selectedDate
    ? getSchedulesForDay(selectedDate).sort((a, b) => (a.time || '').localeCompare(b.time || ''))
    : [];
  const selectedCompanyName = selectedCompany === 'all' ? '전체' : selectedCompany;
  const companyScheduleCount = (name) => allSchedules.filter(s => s.companyName === name).length;

  // 지도뷰: 지역별 그룹핑
  const regionGroups = {};
  schedules.forEach(s => {
    const region = s.companyRegion;
    if (!regionGroups[region]) regionGroups[region] = [];
    regionGroups[region].push(s);
  });

  return (
    <Container>
      <PageHeader>
        <HeaderRight>
          <ViewToggle>
            <ViewBtn $active={viewMode === 'calendar'} onClick={() => setViewMode('calendar')}>
              <IoCalendarOutline size={15} /> 달력
            </ViewBtn>
            <ViewBtn $active={viewMode === 'map'} onClick={() => setViewMode('map')}>
              <IoMapOutline size={15} /> 지도
            </ViewBtn>
          </ViewToggle>
          <AddBtn onClick={() => setShowModal(true)}><IoAdd size={18} /> 일정 등록</AddBtn>
        </HeaderRight>
      </PageHeader>

      <LayoutRow>
        {/* 좌측 파트너 트리 */}
        <TreePanel>
          <TreeHeader onClick={() => setTreeOpen(!treeOpen)}>
            <TreeHeaderLeft>
              <IoBusiness size={16} />
              <span>파트너</span>
            </TreeHeaderLeft>
            {treeOpen ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
          </TreeHeader>

          {treeOpen && (
            <TreeList>
              <TreeItem $active={selectedCompany === 'all'} onClick={() => setSelectedCompany('all')}>
                <TreeDot $color="#4A6741" />
                <TreeName>전체 일정</TreeName>
                <TreeCount>{allSchedules.length}</TreeCount>
              </TreeItem>
              {companies.map(c => (
                <TreeItem key={c.id} $active={selectedCompany === c.id} onClick={() => setSelectedCompany(c.id)}>
                  <TreeDot $color={c.color} />
                  <TreeName>{c.name}</TreeName>
                  <TreeRegion>{c.region}</TreeRegion>
                  <TreeCount>{companyScheduleCount(c.name)}</TreeCount>
                  <ColorBtn onClick={(e) => { e.stopPropagation(); setColorPickerTarget(colorPickerTarget === c.name ? null : c.name); }}>
                    <IoColorPaletteOutline size={14} />
                  </ColorBtn>
                  {colorPickerTarget === c.name && (
                    <ColorPicker onClick={e => e.stopPropagation()}>
                      {PALETTE.map(color => (
                        <ColorSwatch key={color} $color={color} $active={c.color === color} onClick={() => saveCompanyColor(c.name, color)} />
                      ))}
                    </ColorPicker>
                  )}
                </TreeItem>
              ))}
            </TreeList>
          )}
        </TreePanel>

        {/* 우측 메인 영역 */}
        <MainArea>
          {viewMode === 'calendar' ? (
            <>
              <CalendarWrap>
                <FilterTag>
                  {selectedCompanyName} <span>({schedules.length}건)</span>
                </FilterTag>
                <CalHeader>
                  <NavBtn onClick={prevMonth}><IoChevronBack size={20} /></NavBtn>
                  <MonthLabel>{year}년 {month}월</MonthLabel>
                  <NavBtn onClick={nextMonth}><IoChevronForward size={20} /></NavBtn>
                </CalHeader>

                <DowRow>
                  {['일', '월', '화', '수', '목', '금', '토'].map(d => (
                    <DowCell key={d} $sun={d === '일'} $sat={d === '토'}>{d}</DowCell>
                  ))}
                </DowRow>

                {weeks.map((w, wi) => (
                  <WeekRow key={wi}>
                    {w.map((d, di) => {
                      const daySchedules = d ? getSchedulesForDay(d) : [];
                      const isToday = d === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
                      const isSelected = d === selectedDate;
                      return (
                        <DayCell key={di} $empty={!d} $selected={isSelected} onClick={() => d && setSelectedDate(d)}>
                          {d && (
                            <>
                              <DayNum $today={isToday} $sun={di === 0} $sat={di === 6}>{d}</DayNum>
                              <BadgeWrap>
                                {selectedCompany === 'all' ? (
                                  <>
                                    {daySchedules.slice(0, 5).map(s => (
                                      <TagWrap key={s.id}>
                                        <CompanyTag $color={s.color} $hidden={hiddenIds.has(s.id)}>{s.companyName}</CompanyTag>
                                        {hiddenIds.has(s.id) && <HiddenIcon><IoEyeOffOutline size={9} /></HiddenIcon>}
                                      </TagWrap>
                                    ))}
                                    {daySchedules.length > 5 && <MoreBadge>+{daySchedules.length - 5}</MoreBadge>}
                                  </>
                                ) : (
                                  daySchedules.map(s => (
                                    <TagWrap key={s.id}>
                                      <DetailTag $color={s.color} $hidden={hiddenIds.has(s.id)}>
                                        <span>{s.time}</span> {s.title}{s.memo ? ` · ${s.memo}` : ''}
                                      </DetailTag>
                                      {hiddenIds.has(s.id) && <HiddenIcon><IoEyeOffOutline size={9} /></HiddenIcon>}
                                    </TagWrap>
                                  ))
                                )}
                              </BadgeWrap>
                            </>
                          )}
                        </DayCell>
                      );
                    })}
                  </WeekRow>
                ))}
              </CalendarWrap>

              {selectedDate && (
                <Overlay onClick={() => setSelectedDate(null)}>
                  <PopupModal onClick={e => e.stopPropagation()}>
                    <ModalHeader>
                      <ModalTitle>{month}월 {selectedDate}일 — {selectedCompanyName}</ModalTitle>
                      <CloseBtn onClick={() => setSelectedDate(null)}><IoClose size={22} /></CloseBtn>
                    </ModalHeader>
                    <PopupBody>
                      <PopupCount>{month}월 {selectedDate}일 {selectedSchedules.length}건의 일정이 등록되어 있습니다</PopupCount>
                      {selectedSchedules.length === 0 ? (
                        <Empty>등록된 일정이 없습니다</Empty>
                      ) : (
                        selectedSchedules.map(s => (
                          <ScheduleCard key={s.id} $bgColor="#FFFFFF" $hidden={hiddenIds.has(s.id)}>
                            <ScheduleTime>{s.time}</ScheduleTime>
                            <ScheduleInfo>
                              <ScheduleCompany>{s.companyName}</ScheduleCompany>
                              <ScheduleSite>{s.title}{s.memo ? ` · ${s.memo}` : ''}</ScheduleSite>
                              {s.address && <ScheduleAddr>{s.address}</ScheduleAddr>}
                            </ScheduleInfo>
                            <BtnGroup>
                              <HideBtn $hidden={hiddenIds.has(s.id)} onClick={() => toggleHidden(s.id)}>
                                {hiddenIds.has(s.id) ? <><IoEyeOffOutline size={14} /> 숨김</> : <><IoEyeOutline size={14} /> 표시</>}
                              </HideBtn>
                              <DeleteBtn onClick={() => handleDelete(s.id)}>
                                <IoTrashOutline size={14} /> 당일삭제
                              </DeleteBtn>
                              {s.groupId && (
                                <DeleteGroupBtn onClick={() => handleDeleteGroup(s.groupId)}>
                                  <IoTrashOutline size={14} /> 기간삭제
                                </DeleteGroupBtn>
                              )}
                            </BtnGroup>
                          </ScheduleCard>
                        ))
                      )}
                    </PopupBody>
                  </PopupModal>
                </Overlay>
              )}
            </>
          ) : (
            /* ===== 지도 뷰 ===== */
            <MapViewWrap>
              <MapContainer>
                <FilterTag>
                  {selectedCompanyName} <span>({schedules.length}건)</span>
                </FilterTag>
                <MapSvg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  {/* 한반도 남한 윤곽 (단순화) */}
                  <path d="M35,5 L55,4 L62,10 L65,16 L60,20 L55,22 L60,30 L62,35 L58,38 L60,42 L65,45 L68,50 L72,55 L75,60 L78,65 L80,72 L76,78 L70,75 L65,70 L60,68 L55,72 L50,75 L45,72 L40,68 L35,72 L30,68 L25,62 L28,55 L26,48 L30,42 L28,38 L30,32 L28,28 L22,22 L20,16 L25,12 L30,8 Z"
                    fill="#EDF2EB" stroke="#D9DDD6" strokeWidth="0.5" />
                  {/* 제주도 */}
                  <ellipse cx="45" cy="90" rx="10" ry="4" fill="#EDF2EB" stroke="#D9DDD6" strokeWidth="0.5" />

                  {/* 도시 마커 */}
                  {Object.entries(regionGroups).map(([region, items]) => {
                    const pos = CITY_POSITIONS[region];
                    if (!pos) return null;
                    return (
                      <g key={region} style={{ cursor: 'pointer' }} onClick={() => setSelectedRegion(region)}>
                        <circle cx={pos.x} cy={pos.y} r="5" fill="#fff" stroke="#D9DDD6" strokeWidth="0.5" />
                        <text x={pos.x} y={pos.y + 1.2} textAnchor="middle" fontSize="2.8" fill="#2C3640" fontWeight="600">{region}</text>
                        {/* 건수 뱃지 */}
                        <circle cx={pos.x + 4.5} cy={pos.y - 4.5} r="2.5" fill="#E85830" />
                        <text x={pos.x + 4.5} y={pos.y - 3.3} textAnchor="middle" fontSize="2.5" fill="#fff" fontWeight="500">{items.length}</text>
                      </g>
                    );
                  })}
                </MapSvg>
              </MapContainer>
            </MapViewWrap>
          )}

          {/* 지도뷰 지역 팝업 */}
          {selectedRegion && regionGroups[selectedRegion] && (
            <Overlay onClick={() => setSelectedRegion(null)}>
              <PopupModal onClick={e => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle><IoLocationSharp size={18} style={{ marginRight: 6 }} />{selectedRegion} 지역 일정</ModalTitle>
                  <CloseBtn onClick={() => setSelectedRegion(null)}><IoClose size={22} /></CloseBtn>
                </ModalHeader>
                <PopupBody>
                  <PopupCount>{month}월 한 달간 {selectedRegion} 지역 {regionGroups[selectedRegion].length}건의 일정이 등록되어 있습니다</PopupCount>
                  {(() => {
                    const sorted = [...regionGroups[selectedRegion]].sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''));
                    return sorted.map((s, i) => {
                      const showDate = i === 0 || s.date !== sorted[i - 1].date;
                      const isLastOfDate = i === sorted.length - 1 || s.date !== sorted[i + 1].date;
                      const [, m, d] = s.date.split('-');
                      return (
                        <React.Fragment key={s.id}>
                          {showDate && <DateDivider>{Number(m)}월 {Number(d)}일</DateDivider>}
                          <ScheduleCard $bgColor="#FFFFFF" $noBorder={isLastOfDate} $hidden={hiddenIds.has(s.id)}>
                            <ScheduleTime>{s.time}</ScheduleTime>
                            <ScheduleInfo>
                              <ScheduleCompany>{s.companyName}</ScheduleCompany>
                              <ScheduleSite>{s.title}{s.memo ? ` · ${s.memo}` : ''}</ScheduleSite>
                              <ScheduleAddr>{s.address}</ScheduleAddr>
                            </ScheduleInfo>
                            <BtnGroup>
                              <HideBtn $hidden={hiddenIds.has(s.id)} onClick={() => toggleHidden(s.id)}>
                                {hiddenIds.has(s.id) ? <><IoEyeOffOutline size={14} /> 숨김</> : <><IoEyeOutline size={14} /> 표시</>}
                              </HideBtn>
                              <DeleteBtn onClick={() => handleDelete(s.id)}>
                                <IoTrashOutline size={14} /> 당일삭제
                              </DeleteBtn>
                              {s.groupId && (
                                <DeleteGroupBtn onClick={() => handleDeleteGroup(s.groupId)}>
                                  <IoTrashOutline size={14} /> 기간삭제
                                </DeleteGroupBtn>
                              )}
                            </BtnGroup>
                          </ScheduleCard>
                        </React.Fragment>
                      );
                    });
                  })()}
                </PopupBody>
              </PopupModal>
            </Overlay>
          )}
        </MainArea>
      </LayoutRow>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>일정 등록</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FormGroup><FormLabel>업체명</FormLabel>
                <FormInput placeholder="업체명 입력" value={modalForm.companyName} onChange={e => setModalForm(f => ({ ...f, companyName: e.target.value }))} />
              </FormGroup>
              <FormGroup><FormLabel>현장 (제목)</FormLabel>
                <FormInput placeholder="시공 현장" value={modalForm.title} onChange={e => setModalForm(f => ({ ...f, title: e.target.value }))} />
              </FormGroup>
              <FormRow>
                <FormGroup style={{ flex: 1 }}><FormLabel>날짜</FormLabel>
                  <FormInput type="date" value={modalForm.date} onChange={e => setModalForm(f => ({ ...f, date: e.target.value }))} />
                </FormGroup>
                <FormGroup style={{ flex: 1 }}><FormLabel>시간</FormLabel>
                  <FormInput type="time" value={modalForm.time} onChange={e => setModalForm(f => ({ ...f, time: e.target.value }))} />
                </FormGroup>
              </FormRow>
              <FormGroup><FormLabel>주소</FormLabel>
                <FormInput placeholder="현장 주소" value={modalForm.address} onChange={e => setModalForm(f => ({ ...f, address: e.target.value }))} />
              </FormGroup>
              <FormGroup><FormLabel>메모</FormLabel>
                <FormTextarea rows={3} placeholder="메모" value={modalForm.memo} onChange={e => setModalForm(f => ({ ...f, memo: e.target.value }))} />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
              <SubmitBtn disabled={submitting || !modalForm.companyName || !modalForm.title || !modalForm.date} onClick={async () => {
                setSubmitting(true);
                try {
                  await addDoc(collection(db, 'schedules'), {
                    companyName: modalForm.companyName,
                    title: modalForm.title,
                    date: modalForm.date,
                    time: modalForm.time || '09:00',
                    address: modalForm.address,
                    memo: modalForm.memo,
                    isPublic: true,
                    hidden: false,
                    createdAt: serverTimestamp(),
                  });
                  setModalForm({ companyName: '', title: '', date: '', time: '09:00', address: '', memo: '' });
                  setShowModal(false);
                  // 리로드 트리거
                  setMonth(m => m); // force re-render
                  // 직접 리로드
                  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
                  const monthEnd = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
                  const q2 = query(collection(db, 'schedules'), where('date', '>=', monthStart), where('date', '<=', monthEnd));
                  const snap2 = await getDocs(q2);
                  const list2 = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
                  const companyMap2 = {};
                  list2.forEach(s => {
                    const name = s.companyName || '미지정';
                    if (!companyMap2[name]) companyMap2[name] = { name, region: extractRegion(s.address) };
                  });
                  const cl2 = Object.values(companyMap2).map((c, i) => ({ ...c, id: c.name, color: getResolvedColor(c.name, i) }));
                  setCompanies(cl2);
                  setAllSchedules(list2.map(s => {
                    const comp = cl2.find(c => c.name === s.companyName);
                    return { ...s, color: comp?.color || '#8C9AA4', companyRegion: comp?.region || extractRegion(s.address), site: s.title || '', type: '' };
                  }));
                } catch (err) {
                  console.error('일정 등록 실패:', err);
                } finally {
                  setSubmitting(false);
                }
              }}>{submitting ? '등록 중...' : '등록'}</SubmitBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminSchedulePage;

const Container = styled.div`padding: 32px; height: 100%; display: flex; flex-direction: column; overflow: hidden;`;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-shrink: 0;`;
const PageTitle = styled.h1`font-size: 24px; font-weight: 500; color: #1E2A32;`;
const HeaderRight = styled.div`display: flex; align-items: center; gap: 12px;`;
const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 10px;
  background: #fff; color: #1E2A32; font-size: 13px; font-weight: 600;
  border: 1.5px solid #D0D0D0;
  &:hover { background: #F5F5F5; border-color: #999; }
`;

const ViewToggle = styled.div`
  display: flex; background: #fff; border: 1px solid #D9DDD6; border-radius: 10px; overflow: hidden;
`;
const ViewBtn = styled.button`
  display: flex; align-items: center; gap: 5px; padding: 8px 14px; font-size: 13px; font-weight: 400;
  background: ${p => p.$active ? '#4A6741' : 'transparent'};
  color: ${p => p.$active ? '#fff' : '#5A6B75'};
  transition: all 0.15s;
  &:hover { background: ${p => p.$active ? '#4A6741' : '#F5F5F0'}; }
`;

const LayoutRow = styled.div`display: flex; gap: 20px; flex: 1; min-height: 0;`;

/* 트리 패널 */
const TreePanel = styled.div`
  width: 220px; flex-shrink: 0; background: #fff; border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); overflow: hidden;
  height: 100%; display: flex; flex-direction: column;
`;
const TreeHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 16px; cursor: pointer; border-bottom: 1px solid #F0F0EC;
  font-size: 13px; font-weight: 500; color: #1E2A32;
  &:hover { background: #FAFAF7; }
`;
const TreeHeaderLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const TreeList = styled.div`padding: 6px; flex: 1; overflow-y: auto;`;
const TreeItem = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 9px 12px; border-radius: 8px;
  cursor: pointer; font-size: 13px; transition: all 0.12s; position: relative;
  background: ${p => p.$active ? '#EDF2EB' : 'transparent'};
  color: ${p => p.$active ? '#4A6741' : '#5A6B75'};
  font-weight: ${p => p.$active ? 600 : 400};
  &:hover { background: ${p => p.$active ? '#EDF2EB' : '#FAFAF7'}; }
`;
const TreeDot = styled.div`width: 8px; height: 8px; border-radius: 50%; background: ${p => p.$color}; flex-shrink: 0;`;
const TreeName = styled.span`flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; font-weight: 600;`;
const TreeRegion = styled.span`font-size: 11px; color: #8C9AA4;`;
const TreeCount = styled.span`font-size: 11px; color: #8C9AA4; flex-shrink: 0;`;
const ColorBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px; flex-shrink: 0;
  color: #8C9AA4; background: transparent;
  &:hover { background: #F0F0EC; color: #4A6741; }
`;
const ColorPicker = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px; padding: 8px;
  background: #fff; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  position: absolute; top: 100%; left: 8px; z-index: 10; width: 140px;
`;
const ColorSwatch = styled.div`
  width: 22px; height: 22px; border-radius: 50%; cursor: pointer;
  background: ${p => p.$color};
  border: 2px solid ${p => p.$active ? '#1E2A32' : 'transparent'};
  &:hover { transform: scale(1.15); }
  transition: transform 0.1s;
`;

/* 메인 영역 공통 */
const MainArea = styled.div`flex: 1; min-width: 0; height: 100%; display: flex; flex-direction: column;`;
const FilterTag = styled.div`
  font-size: 13px; font-weight: 400; color: #4A6741; margin-bottom: 8px; align-self: flex-start; width: 100%; flex-shrink: 0;
  span { font-weight: 400; color: #8C9AA4; }
`;

/* 캘린더 뷰 */
const CalendarWrap = styled.div`background: #fff; border-radius: 14px; padding: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); flex: 1; overflow-y: auto;`;
const CalHeader = styled.div`display: flex; align-items: center; justify-content: center; gap: 24px; margin-bottom: 20px;`;
const NavBtn = styled.button`display: flex; padding: 6px; border-radius: 8px; color: #5A6B75; &:hover { background: #F5F5F0; }`;
const MonthLabel = styled.div`font-size: 18px; font-weight: 500; color: #1E2A32; min-width: 120px; text-align: center;`;

const DowRow = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); margin-bottom: 4px;`;
const DowCell = styled.div`
  text-align: center; font-size: 12px; font-weight: 400; padding: 8px 0;
  color: ${p => p.$sun ? '#C0392B' : p.$sat ? '#5C6BC0' : '#8C9AA4'};
`;
const WeekRow = styled.div`display: grid; grid-template-columns: repeat(7, 1fr);`;
const DayCell = styled.div`
  min-height: 140px; padding: 8px; border-top: 1px solid #F0F0EC; cursor: ${p => p.$empty ? 'default' : 'pointer'};
  background: ${p => p.$selected ? '#EDF2EB' : 'transparent'}; border-radius: 4px;
  &:hover { background: ${p => !p.$empty && '#FAFAF7'}; }
`;
const DayNum = styled.div`
  font-size: 13px; font-weight: 400; margin-bottom: 4px;
  color: ${p => p.$today ? '#fff' : p.$sun ? '#C0392B' : p.$sat ? '#5C6BC0' : '#1E2A32'};
  ${p => p.$today && `background: #4A6741; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;`}
`;
const BadgeWrap = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const TagWrap = styled.div`
  position: relative; display: inline-flex;
`;
const CompanyTag = styled.div`
  padding: 3px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
  background: ${p => p.$color}; color: #fff; line-height: 1.4;
  opacity: ${p => p.$hidden ? 0.45 : 1};
`;
const HiddenIcon = styled.div`
  position: absolute; top: -4px; right: -4px;
  width: 16px; height: 16px; border-radius: 50%;
  background: #E85830; color: #fff;
  display: flex; align-items: center; justify-content: center;
`;
const DetailTag = styled.div`
  padding: 3px 6px; border-radius: 4px; font-size: 12px; font-weight: 400;
  width: 100%; word-break: keep-all;
  background: #FAFAF7; color: #5A6B75; line-height: 1.4;
  opacity: ${p => p.$hidden ? 0.45 : 1};
  span { font-weight: 600; color: #1E2A32; margin-right: 4px; }
`;
const MoreBadge = styled.div`
  padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 400;
  background: #E8E8E4; color: #8C9AA4; line-height: 1.4;
`;


const PopupModal = styled.div`background: #fff; border-radius: 16px; width: 560px; min-height: 500px; max-height: 70vh; display: flex; flex-direction: column;`;
const PopupBody = styled.div`padding: 20px 24px 24px; overflow-y: auto; flex: 1;`;
const PopupCount = styled.div`font-size: 13px; color: #8C9AA4; margin-bottom: 14px;`;
const Empty = styled.div`text-align: center; padding: 48px; color: #8C9AA4; font-size: 14px;`;
const ScheduleCard = styled.div`
  display: flex; gap: 16px; padding: 14px 16px;
  background: ${p => p.$bgColor || '#FFFFFF'};
  border-bottom: ${p => p.$noBorder ? 'none' : '1px solid #F0F0EC'}; align-items: flex-start;
  opacity: ${p => p.$hidden ? 0.45 : 1};
  &:last-child { border-bottom: none; }
`;
const DateDivider = styled.div`
  font-size: 13px; font-weight: 600; color: #2C3640;
  padding: 12px 0 6px;
  border-top: 1px solid #F0F0EC;
  &:first-of-type { border-top: none; padding-top: 0; }
`;
const ScheduleTime = styled.div`font-size: 16px; font-weight: 500; color: #4A6741; min-width: 110px; white-space: nowrap; flex-shrink: 0;`;
const ScheduleInfo = styled.div`flex: 1; min-width: 0;`;
const ScheduleCompany = styled.div`font-size: 16px; font-weight: 500; color: #1E2A32;`;
const ScheduleSite = styled.div`font-size: 14px; color: #8C9AA4; margin-top: 2px;`;
const ScheduleAddr = styled.div`font-size: 13px; color: #B0B8C0; margin-top: 2px;`;
const BtnGroup = styled.div`display: flex; flex-direction: column; gap: 4px; flex-shrink: 0;`;
const HideBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 10px; border-radius: 8px; flex-shrink: 0;
  font-size: 12px; font-weight: 500;
  background: ${p => p.$hidden ? '#FFF0EC' : '#F4F3F8'};
  color: ${p => p.$hidden ? '#E85830' : '#8C9AA4'};
`;
const DeleteBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 10px; border-radius: 8px; flex-shrink: 0;
  font-size: 12px; font-weight: 500;
  background: #FFF0EC; color: #EF5350;
  &:hover { background: #FFEBE8; }
`;
const DeleteGroupBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 10px; border-radius: 8px; flex-shrink: 0;
  font-size: 12px; font-weight: 500;
  background: #FFF3E0; color: #E65100;
  &:hover { background: #FFECCC; }
`;

/* ===== 지도 뷰 ===== */
const MapViewWrap = styled.div`
  display: flex; height: 100%;
`;
const MapContainer = styled.div`
  flex: 1; background: #fff; border-radius: 14px; padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex;
  flex-direction: column; height: 100%;
`;
const MapSvg = styled.svg`
  flex: 1; width: 100%; height: 100%;
`;

const MapListArea = styled.div`
  width: 340px; flex-shrink: 0; max-height: calc(100vh - 200px);
  overflow-y: auto;
`;
const MapListTitle = styled.h3`font-size: 15px; font-weight: 500; color: #1E2A32; margin-bottom: 14px;`;

const RegionGroup = styled.div`margin-bottom: 16px;`;
const RegionHeader = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 8px 0; margin-bottom: 6px;
  border-bottom: 1px solid #ECEEE9;
`;
const RegionName = styled.span`font-size: 14px; font-weight: 500; color: #1E2A32; flex: 1;`;
const RegionCount = styled.span`font-size: 12px; color: #8C9AA4;`;

const MapScheduleCard = styled.div`
  display: flex; gap: 12px; padding: 12px 14px; border-radius: 10px;
  background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  margin-bottom: 8px; align-items: flex-start;
`;
const MapBadge = styled.div`
  width: 28px; height: 28px; border-radius: 50%; font-size: 12px; font-weight: 500;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$color}; color: #fff; flex-shrink: 0; margin-top: 2px;
`;
const MapCardInfo = styled.div`flex: 1; min-width: 0;`;
const MapCardTop = styled.div`display: flex; justify-content: space-between; align-items: center;`;
const MapCardCompany = styled.span`font-size: 13px; font-weight: 400; color: #1E2A32;`;
const MapCardDate = styled.span`font-size: 11px; color: #8C9AA4;`;
const MapCardSite = styled.div`font-size: 12px; color: #5A6B75; margin-top: 3px;`;
const MapCardAddr = styled.div`font-size: 11px; color: #8C9AA4; margin-top: 2px;`;

/* 모달 */
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const Modal = styled.div`background: #fff; border-radius: 16px; width: 480px;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #F0F0EC;`;
const ModalTitle = styled.h3`font-size: 18px; font-weight: 500;`;
const CloseBtn = styled.button`color: #8C9AA4; display: flex;`;
const ModalBody = styled.div`padding: 24px;`;
const ModalFooter = styled.div`display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #F0F0EC;`;
const FormGroup = styled.div`margin-bottom: 16px;`;
const FormRow = styled.div`display: flex; gap: 12px;`;
const FormLabel = styled.label`display: block; font-size: 13px; font-weight: 400; color: #1E2A32; margin-bottom: 6px;`;
const FormInput = styled.input`width: 100%; padding: 10px 14px; border: none; border-radius: 10px; font-size: 13px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); background: #fff;`;
const FormSelect = styled.select`width: 100%; padding: 10px 14px; border: none; border-radius: 10px; font-size: 13px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.06);`;
const FormTextarea = styled.textarea`width: 100%; padding: 10px 14px; border: none; border-radius: 10px; font-size: 13px; resize: vertical; box-shadow: 0 1px 3px rgba(0,0,0,0.06); background: #fff;`;
const CancelBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #F5F5F0; color: #5A6B75;`;
const SubmitBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #4A6741; color: #fff; font-weight: 400; &:hover { background: #3B5534; } &:disabled { opacity: 0.6; cursor: not-allowed; }`;
